// grouping dayjs imports for clarity
/* eslint-disable simple-import-sort/imports */
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/pt';
import 'dayjs/locale/zh';
/* eslint-enable simple-import-sort/imports */

import isToday from 'dayjs/plugin/isToday';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
/* eslint-enable simple-import-sort/imports */

import * as Font from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect, useState } from 'react';
import { QueryClient, useQueryClient } from 'react-query';

import { Locale, useGetContentQuery, useGetDestinationsQuery } from 'app/generated/hygraph';
import { fetcher as configFetcher, queryKey as configQueryKey } from 'app/hooks/useConfiguration';
import useStore, { useStorePersist } from 'app/hooks/useStore';
import { useI18n } from 'app/providers/I18nProvider';
import { fetchAndActivate, getRemoteConfig, setConfigSettings } from 'app/services/Firebase';
import {
  getBaseURL as getGuestCenterBaseURL,
  setEnv as setGuestCenterEnv,
} from 'app/services/GuestCenterService';
import HygraphService, { HygraphService as THygraphService } from 'app/services/HygraphService';
import Logger from 'app/services/Logger';
import { requestPermissions } from 'app/services/PermissionsService';
import { toMs } from 'app/utils/time';
import { LogBox, Platform } from 'react-native';

const MODULE = '[useBootstrap]';
LogBox.ignoreLogs([
  'NativeBase: The contrast ratio of 1:1 for black on transparent',
  'specify an aria-label for accessibility at Radio', // this is getting spammed for RadioButtonCard even though it has an aria-label
]);

dayjs.extend(localeData);
dayjs.extend(isToday);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

// Keep the native splash up until Main has rendered the first screen. Called at module load so it
// runs before React's first (empty) content can auto-hide the native splash.
SplashScreen.preventAutoHideAsync();

const hygraphService = HygraphService();
const { client } = hygraphService;

/**
 * Prepares the app for startup: global dependencies, static assets,
 * loading locally cached data, prefetching remote data and config, etc.
 */
export default function useBootstrap() {
  const queryClient = useQueryClient();
  const { locale } = useI18n();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    dayjs.locale(locale);
  }, [locale]);

  setGuestCenterClientEnv(queryClient);
  setCmsStage(hygraphService);

  // Load any resources or data that we need prior to rendering the app
  useEffect(() => {
    (async () => {
      try {
        await bootstrap({ queryClient, locale });
        // web defers these until sign in, see useRequestPermissions
        if (Platform.OS !== 'web') await requestPermissions();
      } catch (err) {
        Logger.error(`${MODULE} bootstrap failed`, { err });
      } finally {
        setIsReady(true);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isReady, bootstrap: () => bootstrap({ queryClient, locale }) };
}

type BootstrapProps = {
  queryClient: QueryClient;
  locale: Locale;
};

function bootstrap({ queryClient, locale }: BootstrapProps) {
  return Promise.all([
    ...loadCachedData(),
    ...prefetchRemoteData({ queryClient, locale }),
    loadRemoteConfig(),
  ]);
}

function loadCachedData(): Promise<void>[] {
  const loadFonts = Font.loadAsync({
    /* eslint-disable global-require */
    ProximaNova: require('../../assets/fonts/ProximaNova.ttf'),
    'ProximaNova-Bold': require('../../assets/fonts/ProximaNova-Bold.ttf'),
    /* eslint-enable global-require */
  });
  const hydrateStore = new Promise<void>((resolve) => {
    let timeout: NodeJS.Timeout;
    const unsubscribe = useStorePersist.onFinishHydration(() => {
      Logger.debug('[bootstrap] store hydration finish');
      clearTimeout(timeout);
      resolve();
    });
    timeout = setTimeout(() => {
      Logger.debug('[bootstrap] store hydration timeout');
      unsubscribe();
      resolve();
    }, 3000);
  });

  return [loadFonts, hydrateStore];
}

function prefetchRemoteData({ queryClient, locale }: BootstrapProps): Promise<void>[] {
  const prefetchContent = queryClient.prefetchQuery(
    useGetContentQuery.getKey({ locale }),
    useGetContentQuery.fetcher(client, { locale })
  );
  const prefetchConfiguration = queryClient.prefetchQuery(configQueryKey, configFetcher);
  const prefetchDestinations = queryClient.prefetchQuery(
    useGetDestinationsQuery.getKey({ locale }),
    useGetDestinationsQuery.fetcher(client, { locale })
  );

  return [prefetchContent, prefetchDestinations, prefetchConfiguration];
}

function setGuestCenterClientEnv(queryClient: QueryClient) {
  const { env } = useStore.getState();
  const prevBaseURL = getGuestCenterBaseURL();
  setGuestCenterEnv(env);
  const hasChanged = getGuestCenterBaseURL() !== prevBaseURL;
  if (hasChanged) {
    queryClient.cancelQueries();
    queryClient.invalidateQueries();
    queryClient.clear();
  }
}

function setCmsStage(service: THygraphService) {
  const { cmsStage } = useStore.getState();
  service.setStage(cmsStage);
}

async function loadRemoteConfig() {
  await setConfigSettings(getRemoteConfig(), {
    minimumFetchIntervalMillis: __DEV__ ? toMs(5, 'm') : toMs(12, 'h'),
  });

  return fetchAndActivate(getRemoteConfig()).catch((err: unknown) => {
    // Error instances serialize to {} in JSON, so log the message and the native error code.
    Logger.warn(`${MODULE} failed to fetch and activate remote config`, {
      message: err instanceof Error ? err.message : String(err),
      code: (err as { code?: unknown } | null)?.code,
    });
  });
}
